import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Toggle } from "@/components/ui/toggle";
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Rocket, Store, Layout, MonitorPlay, Handshake, Car, Building2 } from 'lucide-react';

export const ProductSettingsSection = () => {
    const [settings, setSettings] = useState({
        show_watch_ads: true,
        show_creatives: true,
        show_boost: true,
        show_venue_ads: true,
        show_partner_program: true,
        ads_urban_enabled: true,
        ads_venue_enabled: true
    });
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const { data, error } = await supabase
                .from('system_settings')
                .select('*')
                .single();

            if (error) {
                console.warn("Erro ao buscar configurações ou tabela inexistente:", error);
                return;
            }
            if (data) setSettings(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const updateSetting = async (key: string, value: boolean) => {
        setSettings(prev => ({ ...prev, [key]: value }));

        try {
            // Verifica se já existe ao menos 1 registro, se não, tenta inserir (fallback de segurança)
            // Mas assumes-se que a tabela foi migrada.
            const { data: existing } = await supabase.from('system_settings').select('id').limit(1).single();

            let error;
            if (existing) {
                const result = await supabase
                    .from('system_settings')
                    .update({ [key]: value })
                    .eq('id', existing.id);
                error = result.error;
            } else {
                // Se tabela vazia, insere primeiro registro
                const result = await supabase
                    .from('system_settings')
                    .insert({
                        show_watch_ads: true,
                        show_creatives: true,
                        show_boost: true,
                        show_venue_ads: true,
                        show_partner_program: true,
                        ads_urban_enabled: true,
                        ads_venue_enabled: true,
                        [key]: value
                    });
                error = result.error;
            }

            if (error) throw error;

            toast({
                title: "Configuração atualizada",
                description: "Visibilidade alterada com sucesso."
            });
        } catch (error) {
            console.error("Erro ao atualizar:", error);
            toast({
                title: "Erro",
                description: "Não foi possível salvar a alteração.",
                variant: 'destructive',
            });
            fetchSettings(); // Reverter
        }
    };

    const ToggleItem = ({ id, label, description, icon: Icon, checked, onChangeKey }: any) => (
        <div className={`flex items-center justify-between p-4 bg-white rounded-xl border transition-all duration-200 ${checked ? 'border-emerald-200 ring-1 ring-emerald-50/50' : 'border-slate-100'}`}>
            <div className="flex items-start gap-4">
                <div className={`p-2.5 rounded-xl transition-colors ${checked ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex flex-col">
                    <Label htmlFor={id} className="font-bold text-slate-900 text-base cursor-pointer">
                        {label}
                    </Label>
                    <span className="text-xs text-slate-500 font-medium">{description}</span>
                </div>
            </div>
            <Toggle
                id={id}
                checked={checked}
                onChange={(c) => updateSetting(onChangeKey, c)}
                className={checked ? "bg-emerald-500" : "bg-slate-200"}
                aria-label={label}
            />
        </div>
    );

    if (loading) {
        return (
            <div className="p-12 text-center">
                <div className="w-8 h-8 border-3 border-emerald-100 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-slate-500 font-medium font-outfit">Carregando configurações...</p>
            </div>
        );
    }

    return (
        <Card className="w-full bg-slate-50/50 border-none shadow-none">
            <CardHeader className="px-0 pt-0 pb-6">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-emerald-600 rounded-2xl text-white shadow-lg shadow-emerald-200/50">
                        <Rocket className="w-6 h-6" />
                    </div>
                    <div>
                        <CardTitle className="text-2xl text-slate-800 font-black tracking-tight">Visibilidade de Produtos</CardTitle>
                        <CardDescription className="text-slate-500 font-medium">
                            Ative ou desative recursos específicos no dashboard dos usuários.
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="px-0 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ToggleItem
                        id="boost"
                        label="Impulsionar"
                        description="Exibir seção 'Impulsionar' no menu lateral"
                        icon={Rocket}
                        checked={settings.show_boost}
                        onChangeKey="show_boost"
                    />

                    <ToggleItem
                        id="watch-ads"
                        label="Watch ADS"
                        description="Exibir botão Watch ADS (Impulsionar)"
                        icon={Store}
                        checked={settings.show_watch_ads}
                        onChangeKey="show_watch_ads"
                    />

                    <ToggleItem
                        id="creatives"
                        label="Criativos"
                        description="Exibir botão Criativos (Impulsionar)"
                        icon={Layout}
                        checked={settings.show_creatives}
                        onChangeKey="show_creatives"
                    />

                    <ToggleItem
                        id="partner"
                        label="Seja um Parceiro"
                        description="Exibir seção 'Seja um Parceiro' no menu lateral"
                        icon={Handshake}
                        checked={settings.show_partner_program}
                        onChangeKey="show_partner_program"
                    />

                    <ToggleItem
                        id="venue-ads"
                        label="Venue ADS"
                        description="Exibir botão de Venue ADS (Parceiro)"
                        icon={MonitorPlay}
                        checked={settings.show_venue_ads}
                        onChangeKey="show_venue_ads"
                    />
                </div>

                {/* Separador visual */}
                <div className="py-4 flex items-center gap-4">
                    <div className="h-px bg-slate-200 flex-1" />
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2 whitespace-nowrap">Tipos de ADS</h4>
                    <div className="h-px bg-slate-200 flex-1" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ToggleItem
                        id="ads-urban-enabled"
                        label="ADS Urban"
                        description="Veiculação em veículos de mobilidade"
                        icon={Car}
                        checked={settings.ads_urban_enabled}
                        onChangeKey="ads_urban_enabled"
                    />

                    <ToggleItem
                        id="ads-venue-enabled"
                        label="ADS Venue"
                        description="Veiculação em estabelecimentos fixos"
                        icon={Building2}
                        checked={settings.ads_venue_enabled}
                        onChangeKey="ads_venue_enabled"
                    />
                </div>
            </CardContent>
        </Card>
    );
};
